import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import Card from "../../../components/Card/Card";
import Select from "../../../components/Select/Select";
import Input from "../../../components/Input/Input";
import Button from "../../../components/Button/Button";
import Loader from "../../../components/Loader/Loader";
import Switch from "../../../components/Switch/Switch";
import AssignmentGrid from "../../../components/AssignmentGrid/AssignmentGrid";
import { getPrograms } from "../../../services/ProgramServices";
import { getSurveyFolders } from "../../../services/SurveyFolderService";
import {
  getAvailableSurveys,
  getAssignableStudents,
  createSurvey,
  getSurveyById,
  updateSurvey,
} from "../../../services/SurveyService";
import ImportSurveySteps from "./ImportSurveySteps";
// import SurveyRequestPreview from "../SurveyRequestPreview";
import ImportSurveyAudienceTable from "./ImportSurveyAudienceTable";
import "../../dashboard/Dashboard.css";
import "./ImportSurvey.css";

const ImportSurvey = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [editingSurveyTitle, setEditingSurveyTitle] = useState("");
  const [folders, setFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(true);
  const [folderId, setFolderId] = useState("");
  const [surveysInFolder, setSurveysInFolder] = useState([]);
  const [surveysLoading, setSurveysLoading] = useState(false);
  const [surveyId, setSurveyId] = useState("");
  const [classes, setClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(true);
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [audienceType, setAudienceType] = useState("whole");
  const [studentsByClass, setStudentsByClass] = useState({});
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("17:00");
  const [isActive, setIsActive] = useState(true);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const selectedFolder = useMemo(
    () => folders.find((f) => f.id === Number(folderId)) || null,
    [folders, folderId],
  );

  useEffect(() => {
    if (isEditMode) {
      setFoldersLoading(false);
      return;
    }
    const fetchFolders = async () => {
      try {
        const response = await getSurveyFolders({ status: 1 });
        if (response.status === 200) {
          setFolders(response.data.data || []);
        }
      } catch (error) {
        console.error("API Error:", error.response);
      } finally {
        setFoldersLoading(false);
      }
    };
    fetchFolders();
  }, [isEditMode]);

  useEffect(() => {
    if (!isEditMode) return;
    const fetchSurvey = async () => {
      try {
        setInitialLoading(true);
        const response = await getSurveyById(id);
        if (response.status === 200) {
          const survey = response.data.data;
          setEditingSurveyTitle(survey.title || "");
          setSelectedClassIds(
            (survey.programs || [])
              .map((p) => p.id ?? p.program_id ?? p.program?.id)
              .filter(Boolean)
              .map(String),
          );
          setAudienceType(survey.is_student_specific ? "selected" : "whole");
          setSelectedStudentIds((survey.students || []).map((s) => s.student_id));
          setStartDate(survey.start_date || "");
          setEndDate(survey.end_date || "");
          setStartTime((survey.start_time || "09:00:00").slice(0, 5));
          setEndTime((survey.end_time || "17:00:00").slice(0, 5));
          setIsActive(Number(survey.status) === 1);
        }
      } catch (error) {
        console.error("API Error:", error.response);
        enqueueSnackbar("Could not load the survey", { variant: "error" });
      } finally {
        setInitialLoading(false);
      }
    };
    fetchSurvey();
  }, [id, isEditMode]);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await getPrograms();
        if (response.success) {
          setClasses(response.data);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setClassesLoading(false);
      }
    };
    fetchClasses();
  }, []);

  useEffect(() => {
    if (!selectedFolder) {
      setSurveysInFolder([]);
      return undefined;
    }

    let cancelled = false;
    const fetchFolderSurveys = async () => {
      try {
        setSurveysLoading(true);
        const response = await getAvailableSurveys({
          page: 1,
          per_page: 50,
          folder_name: selectedFolder.name,
        });
        if (!cancelled && response.status === 200) {
          const payload = response.data.data;
          const rows = Array.isArray(payload) ? payload : (payload?.data || []);
          setSurveysInFolder(rows);
        }
      } catch (error) {
        console.error("API Error:", error.response);
        if (!cancelled) setSurveysInFolder([]);
      } finally {
        if (!cancelled) setSurveysLoading(false);
      }
    };
    fetchFolderSurveys();
    return () => {
      cancelled = true;
    };
  }, [selectedFolder]);

  useEffect(() => {
    if (audienceType !== "selected" || selectedClassIds.length === 0) {
      return undefined;
    }

    let cancelled = false;
    const fetchStudents = async () => {
      try {
        setStudentsLoading(true);
        const ids = selectedClassIds.map(Number);
        const response = await getAssignableStudents({
          ...(isEditMode ? { survey_id: id, assigned: "all" } : {}),
          program_ids: ids,
        });
        if (cancelled) return;

        const rows = Array.isArray(response.data.data)
          ? response.data.data
          : response.data.data?.data || [];

        const merged = {};
        ids.forEach((classId) => {
          merged[classId] = rows
            .filter((s) => Number(s.program_id) === classId)
            .map((s) => ({
              id: s.student_id ?? s.id,
              name: s.name || `${s.first_name || ""} ${s.last_name || ""}`.trim(),
              admissionNo: s.id_number,
              rollNo: s.roll_no,
            }));
        });
        setStudentsByClass(merged);
      } catch (error) {
        if (error.response?.status !== 404) {
          console.error("API Error:", error.response);
        }
        if (!cancelled) setStudentsByClass({});
      } finally {
        if (!cancelled) setStudentsLoading(false);
      }
    };
    fetchStudents();
    return () => {
      cancelled = true;
    };
  }, [audienceType, selectedClassIds, isEditMode, id]);

  const handleFolderChange = (value) => {
    setFolderId(value);
    setSurveyId("");
  };

  const handleManageClick = () => {
    navigate("/folder");
  };

  const handleClassToggle = (event) => {
    const classId = event.target.value;
    setSelectedClassIds((prev) =>
      event.target.checked
        ? [...prev, classId]
        : prev.filter((id) => id !== classId),
    );
  };

  const handleClassSelectAll = (event) => {
    setSelectedClassIds(
      event.target.checked ? classes.map((c) => c.id.toString()) : [],
    );
  };

  const handleAudienceTypeChange = (type) => {
    setAudienceType(type);
    if (type === "whole") setSelectedStudentIds([]);
  };

  const handleToggleStudent = (studentId) => {
    setSelectedStudentIds((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId],
    );
  };

  const handleToggleAllStudents = (students, checked) => {
    const ids = students.map((s) => s.id);
    setSelectedStudentIds((prev) =>
      checked
        ? Array.from(new Set([...prev, ...ids]))
        : prev.filter((id) => !ids.includes(id)),
    );
  };

  const startDateMissing = !startDate;
  const endDateInvalid = !endDate || (startDate && endDate < startDate);

  const startDateError = submitAttempted && startDateMissing ? "Start date is required." : "";
  const endDateError = submitAttempted && !endDate
    ? "End date is required."
    : startDate && endDate && endDate < startDate
      ? "End date cannot be before the start date."
      : "";

  const canImport = Boolean(
    (isEditMode || (selectedFolder && surveyId)) &&
    selectedClassIds.length > 0 &&
    !startDateMissing &&
    !endDateInvalid &&
    (audienceType !== "selected" || selectedStudentIds.length > 0),
  );

  const requestPayload = useMemo(
    () => ({
      ...(isEditMode ? {} : { sm_survey_id: surveyId || "(pick a survey)" }),
      status: isActive ? 1 : 2,
      program_ids: selectedClassIds.map(Number),
      student_ids: audienceType === "selected" ? selectedStudentIds : [],
      start_date: startDate,
      start_time: startTime,
      end_date: endDate,
      end_time: endTime,
    }),
    [
      isEditMode,
      isActive,
      surveyId,
      selectedClassIds,
      audienceType,
      selectedStudentIds,
      startDate,
      startTime,
      endDate,
      endTime,
    ],
  );

  const handleImport = async () => {
    setSubmitAttempted(true);
    if (!canImport || submitting) return;

    try {
      setSubmitting(true);
      const response = isEditMode
        ? await updateSurvey(id, requestPayload)
        : await createSurvey(requestPayload);
      if (response.status === 200 || response.status === 201) {
        enqueueSnackbar(response.data.message.success, { variant: "success" });
        navigate("/surveys");
      } else {
        enqueueSnackbar(response.data.message.error, { variant: "error" });
      }
    } catch (error) {
      console.error("API Error:", error.response);
      const errors = error.response?.data?.message?.errors;
      const firstError = errors && Object.values(errors)[0]?.[0];
      enqueueSnackbar(firstError || "Something went wrong", {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (initialLoading) {
    return (
      <Card componentName="Edit survey" back={() => navigate("/surveys")}>
        <Loader />
      </Card>
    );
  }

  return (
    <Card
      componentName={isEditMode ? `Edit survey — ${editingSurveyTitle}` : "Import a survey from SurveyMonkey"}
      back={() => navigate("/surveys")}
    >
      {!isEditMode && (
        <ImportSurveySteps
          reachedIndex={2}
          currentIndex={2}
          onStepClick={() => {}}
        />
      )}

      <div className="import-panel">
        <div className="import-panel-title">{isEditMode ? "Survey" : "Folder & survey"}</div>
        <div className="import-panel-body">
          {isEditMode ? (
            <div className="import-field-row">
              <div className="import-field-grow">
                <Input
                  label="Title"
                  type="text"
                  name="surveyTitle"
                  value={editingSurveyTitle}
                  disabled
                  onChangeHandler={() => {}}
                />
              </div>
            </div>
          ) : (
            <>
              <div className="import-field-row">
                <div className="import-field-grow">
                  <label className="import-field-label">
                    Folder{" "}
                    <span className="import-field-label-muted">· saved in EVS</span>
                  </label>
                  {foldersLoading ? (
                    <Loader />
                  ) : (
                    <Select
                      name="folder"
                      value={folderId}
                      placeholder="Select a folder"
                      options={folders.map((f) => ({ id: f.id, name: f.name }))}
                      onChangeHandler={(e) => handleFolderChange(e.target.value)}
                    />
                  )}
                </div>
                <Button variant="outline-secondary" onClick={handleManageClick}>
                  Manage
                </Button>
              </div>

              {selectedFolder &&
                (surveysLoading ? (
                  <Loader />
                ) : (
                  <Select
                    label="Survey"
                    name="survey"
                    value={surveyId}
                    placeholder="Select a published survey"
                    options={surveysInFolder.map((s) => ({
                      id: s.id,
                      name: s.title,
                    }))}
                    onChangeHandler={(e) => setSurveyId(e.target.value)}
                  />
                ))}
            </>
          )}
        </div>

        <div className="import-panel-title">Target classes</div>
        <div className="import-panel-body">
          {classesLoading ? (
            <Loader />
          ) : (
            <AssignmentGrid
              items={classes}
              selectedIds={selectedClassIds}
              onToggle={handleClassToggle}
              onSelectAll={handleClassSelectAll}
              switchName="target_class_ids"
            />
          )}

          <div className="import-panel-subtitle">Audience</div>
          <div className="import-audience-options">
            <div
              className={`import-audience-option ${audienceType === "whole" ? "selected" : ""}`}
              onClick={() => handleAudienceTypeChange("whole")}
            >
              <input type="radio" readOnly checked={audienceType === "whole"} />
              <div>
                <div className="import-audience-option-title">Whole class</div>
                <div className="import-audience-option-desc">
                  Every non-suspended student of the ticked classes.
                </div>
              </div>
            </div>
            <div
              className={`import-audience-option ${audienceType === "selected" ? "selected" : ""}`}
              onClick={() => handleAudienceTypeChange("selected")}
            >
              <input
                type="radio"
                readOnly
                checked={audienceType === "selected"}
              />
              <div>
                <div className="import-audience-option-title">
                  Selected students
                </div>
                <div className="import-audience-option-desc">
                  Pick individual students from those classes.
                </div>
              </div>
            </div>
          </div>

          {audienceType === "selected" && (
            <div className="import-audience-inline">
              {selectedClassIds.length === 0 ? (
                <p className="import-panel-hint">
                  Tick at least one class above to pick students.
                </p>
              ) : studentsLoading ? (
                <Loader />
              ) : (
                <>
                  <p className="import-panel-hint">
                    Pick the individual students who should receive this survey.
                  </p>
                  <ImportSurveyAudienceTable
                    classes={classes}
                    classIds={selectedClassIds.map(Number)}
                    studentsByClass={studentsByClass}
                    selectedStudentIds={selectedStudentIds}
                    onToggleStudent={handleToggleStudent}
                    onToggleAll={handleToggleAllStudents}
                  />
                  <div className="import-audience-count">
                    {selectedStudentIds.length} student(s) selected
                  </div>
                </>
              )}
            </div>
          )}

          <div className="import-date-row">
            <Input
              type="date"
              label="Start date"
              name="startDate"
              required
              value={startDate}
              onChangeHandler={(e) => setStartDate(e.target.value)}
              error={startDateError}
            />
            <Input
              type="date"
              label="End date"
              name="endDate"
              required
              value={endDate}
              onChangeHandler={(e) => setEndDate(e.target.value)}
              error={endDateError}
              min={startDate || undefined}
            />
          </div>

          <div className="import-date-row">
            <Input
              type="time"
              label="Start time"
              name="startTime"
              value={startTime}
              onChangeHandler={(e) => setStartTime(e.target.value)}
            />
            <Input
              type="time"
              label="End time"
              name="endTime"
              value={endTime}
              onChangeHandler={(e) => setEndTime(e.target.value)}
            />
          </div>

          <div className="import-checkbox-row">
            <Switch
              name="status"
              label={isActive ? "Active" : "Inactive"}
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
          </div>

         {/* <SurveyRequestPreview
            path="/api/v1/surveys"
            payload={requestPayload}
          />*/}
        </div>
      </div>

      <div className="import-footer">
        <Button variant="light" onClick={() => navigate("/surveys")}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={!canImport || submitting}
          onClick={handleImport}
        >
          {isEditMode
            ? (submitting ? "Saving..." : "Save changes")
            : (submitting ? "Importing..." : "Import Survey")}
        </Button>
      </div>
    </Card>
  );
};

export default ImportSurvey;
